import { flexRender, type Row } from '@tanstack/react-table';
import type { VirtualItem } from '@tanstack/react-virtual';

import { TableBody, TableCell, TableRow } from '@/_shared/components/ui/table';
import { cn } from '@/_shared/lib/utils';

import type { TableColumnMeta } from './types';
import { alignClass } from './utils';

interface TableRowsProps<T> {
  rows: Row<T>[];
  virtualize: boolean;
  virtualItems: VirtualItem[];
  totalSize: number;
  // virtualizer measures real heights of each row from the DOM
  measureElement: (node: HTMLTableRowElement | null) => void;
  visibleColumnCount: number;
  headerRowCount: number;
  rowClassName?: (row: T, index: number) => string;
  onRowClick?: (row: T, index: number) => void;
  /** `getRowId`'s id of the currently-selected row, already gated on `getRowId` being present (see `Table.tsx`). */
  selectedRowId?: string | null;
  emptyState: React.ReactNode;
  /** Offset of the table within an external scroller; 0 when the table owns its viewport. */
  scrollMargin?: number;
}

const ROW_CLICK_IGNORE = 'button, a, input, select, textarea, [role="menuitem"]';

/**
 * Pointer click + selection state for one row.
 *
 * Click props are empty when there's no handler. Selection is only ever reflected when
 * `selectedRowId` is non-null (the caller has a selection concept in play, per `Table.tsx`'s gate on
 * `getRowId`) — then EVERY row gets `aria-selected` (true on the match, false on the rest), so
 * assistive tech reads selection as a property of the set, not just a lone `true` on one row. The
 * matching row also gets `data-state="selected"`, which the shadcn primitive already styles
 * (`ui/table.tsx`'s `data-[state=selected]:bg-muted`).
 */
function rowInteraction<T>(
  row: Row<T>,
  onRowClick?: (row: T, index: number) => void,
  selectedRowId?: string | null,
): { props: React.HTMLAttributes<HTMLTableRowElement> & { 'data-state'?: 'selected' }; cls: string } {
  const isSelected = selectedRowId != null && row.id === selectedRowId;
  const selectionProps =
    selectedRowId != null
      ? { 'aria-selected': isSelected, 'data-state': isSelected ? ('selected' as const) : undefined }
      : {};
  if (!onRowClick) return { props: selectionProps, cls: '' };
  return {
    props: {
      ...selectionProps,
      onClick: (e) => {
        if ((e.target as HTMLElement).closest(ROW_CLICK_IGNORE)) return;
        onRowClick(row.original, row.index);
      },
    },
    cls: 'group cursor-pointer hover:bg-muted/50',
  };
}

function renderRowCells<T>(row: Row<T>): React.ReactNode {
  return row.getVisibleCells().map((cell) => {
    const meta = cell.column.columnDef.meta as TableColumnMeta | undefined;
    const content = flexRender(cell.column.columnDef.cell, cell.getContext());
    // Cells are vertically centered (the primitive's `align-middle`), wrapping or not: when one tall
    // multi-line cell sets the row height, the short cells beside it read better centered against it
    // than pinned to the top.
    return (
      <TableCell
        key={cell.id}
        className={cn('border-b border-border', alignClass(meta?.align), meta?.wrap && 'whitespace-normal')}
      >
        {content}
      </TableCell>
    );
  });
}

function PlainRows<T>({
  rows,
  rowClassName,
  onRowClick,
  selectedRowId,
}: Pick<TableRowsProps<T>, 'rows' | 'rowClassName' | 'onRowClick' | 'selectedRowId'>): React.ReactElement {
  return (
    <TableBody>
      {rows.map((row) => {
        const { props, cls } = rowInteraction(row, onRowClick, selectedRowId);
        return (
          <TableRow key={row.id} className={cn(cls, rowClassName?.(row.original, row.index))} {...props}>
            {renderRowCells(row)}
          </TableRow>
        );
      })}
    </TableBody>
  );
}

function VirtualRows<T>({
  rows,
  virtualItems,
  totalSize,
  measureElement,
  visibleColumnCount,
  headerRowCount,
  rowClassName,
  onRowClick,
  selectedRowId,
  scrollMargin,
}: Pick<
  TableRowsProps<T>,
  | 'rows'
  | 'virtualItems'
  | 'totalSize'
  | 'measureElement'
  | 'visibleColumnCount'
  | 'headerRowCount'
  | 'rowClassName'
  | 'onRowClick'
  | 'selectedRowId'
  | 'scrollMargin'
>): React.ReactElement {
  // Pixel offsets come straight from the virtualizer's measurements — px is
  // required here (these are runtime-measured layout values, not design tokens).
  // Item offsets are measured from the *scroller's* origin, so discount the table's own offset within it;
  // `getTotalSize()` already excludes the margin, hence the asymmetry. Both are no-ops at margin 0.
  const margin = scrollMargin ?? 0;
  const paddingTop = virtualItems.length > 0 ? virtualItems[0]!.start - margin : 0;
  const paddingBottom = virtualItems.length > 0 ? totalSize - (virtualItems[virtualItems.length - 1]!.end - margin) : 0;

  return (
    <TableBody>
      {paddingTop > 0 && (
        <tr role="presentation">
          <td colSpan={visibleColumnCount} style={{ height: `${paddingTop}px`, padding: 0, border: 0 }} />
        </tr>
      )}

      {virtualItems.map((virtualRow) => {
        const row = rows[virtualRow.index]!;
        const { props, cls } = rowInteraction(row, onRowClick, selectedRowId);
        return (
          <TableRow
            key={row.id}
            data-index={virtualRow.index}
            aria-rowindex={virtualRow.index + 1 + headerRowCount}
            ref={measureElement}
            className={cn(cls, rowClassName?.(row.original, row.index))}
            {...props}
          >
            {renderRowCells(row)}
          </TableRow>
        );
      })}

      {paddingBottom > 0 && (
        <tr role="presentation">
          <td colSpan={visibleColumnCount} style={{ height: `${paddingBottom}px`, padding: 0, border: 0 }} />
        </tr>
      )}
    </TableBody>
  );
}

export function TableRows<T>({
  rows,
  virtualize,
  virtualItems,
  totalSize,
  measureElement,
  visibleColumnCount,
  headerRowCount,
  rowClassName,
  onRowClick,
  selectedRowId,
  emptyState,
  scrollMargin,
}: TableRowsProps<T>): React.ReactElement {
  if (rows.length === 0) {
    return (
      <TableBody>
        <TableRow className="hover:bg-transparent">
          <TableCell colSpan={visibleColumnCount} className="py-8 text-center text-sm text-muted-foreground">
            {emptyState}
          </TableCell>
        </TableRow>
      </TableBody>
    );
  }

  return virtualize ? (
    <VirtualRows
      rows={rows}
      virtualItems={virtualItems}
      totalSize={totalSize}
      measureElement={measureElement}
      visibleColumnCount={visibleColumnCount}
      headerRowCount={headerRowCount}
      rowClassName={rowClassName}
      onRowClick={onRowClick}
      selectedRowId={selectedRowId}
      scrollMargin={scrollMargin}
    />
  ) : (
    <PlainRows rows={rows} rowClassName={rowClassName} onRowClick={onRowClick} selectedRowId={selectedRowId} />
  );
}
